import { CreateCompanyDialog } from "@/components/CreateCompanyDialog";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { Building2, Globe, MapPin, Plus, Search, Users } from "lucide-react";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";

// ============================================================
// Companies Page — Firmen-Übersicht + Suche
// ============================================================

interface CompanyListResponse {
	data: Array<{
		company: {
			id: string;
			name: string;
			branche: string | null;
			beschreibung: string | null;
			website: string | null;
			mitarbeiter: string | null;
			hauptsitz: string | null;
			tags: string | null;
			createdAt: string;
		};
		bookingCount: number;
		contactCount: number;
	}>;
	total: number;
	limit: number;
	offset: number;
}

export function CompaniesPage() {
	const [params, setParams] = useSearchParams();
	const [showDetail, setShowDetail] = useState<string | null>(null);
	const [showCreate, setShowCreate] = useState(false);

	const q = params.get("q") ?? "";
	const page = Number(params.get("page") ?? "0");

	const query = useQuery({
		queryKey: ["companies", { q, page }],
		queryFn: () => {
			const p = new URLSearchParams();
			if (q) p.set("search", q);
			p.set("offset", String(page * 50));
			p.set("limit", "50");
			return api.get<CompanyListResponse>(`/companies?${p.toString()}`);
		},
	});

	const companies = query.data?.data ?? [];
	const total = query.data?.total ?? 0;

	function setFilter(key: string, value: string) {
		const next = new URLSearchParams(params);
		if (value) next.set(key, value);
		else next.delete(key);
		next.delete("page");
		setParams(next);
	}

	return (
		<div className="space-y-4 animate-fade-in">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div>
					<h1 className="font-display text-2xl">Firmen</h1>
					<p className="text-sm text-stone-500">{total} gesamt</p>
				</div>
				<button
					type="button"
					onClick={() => setShowCreate(true)}
					className="btn-primary"
				>
					<Plus size={16} /> Neue Firma
				</button>
			</div>

			{showCreate && (
				<CreateCompanyDialog onClose={() => setShowCreate(false)} />
			)}

			{/* Search */}
			<div className="relative">
				<Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
				<input
					type="text"
					value={q}
					onChange={(e) => setFilter("q", e.target.value)}
					placeholder="Firmenname, Branche suchen..."
					className="input pl-9"
				/>
			</div>


			{/* Error State */}
			{query.isError && (
				<div className="card border-red-200 bg-red-50 text-red-700 text-sm">
					Fehler beim Laden der Firmen.{" "}
					<button type="button" onClick={() => query.refetch()} className="underline">
						Erneut versuchen
					</button>
				</div>
			)}

			{/* List */}
			<div className="space-y-2">
				{companies.map(({ company: c, bookingCount, contactCount }) => (
					<div
						key={c.id}
						className="card hover:shadow-sm transition-shadow cursor-pointer"
						onClick={() => setShowDetail(showDetail === c.id ? null : c.id)}
						onKeyDown={(e) => {
							if (e.key === "Enter" || e.key === " ")
								setShowDetail(showDetail === c.id ? null : c.id);
						}}
					>
						<div className="flex items-center gap-3">
							<div className="h-10 w-10 rounded-full bg-stone-100 text-stone-600 flex items-center justify-center shrink-0">
								<Building2 size={18} />
							</div>
							<div className="flex-1 min-w-0">
								<p className="font-medium text-stone-900 truncate">{c.name}</p>
								<div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-stone-500">
									{c.branche && <span>{c.branche}</span>}
									{c.hauptsitz && (
										<span className="inline-flex items-center gap-1">
											<MapPin size={10} /> {c.hauptsitz}
										</span>
									)}
								</div>
							</div>
							<div className="flex gap-3 text-right shrink-0">
								<span className="badge bg-stone-100 text-stone-600">
									{contactCount} Kontakte
								</span>
								<span className="badge bg-brand-50 text-brand-700">
									{bookingCount} Buchungen
								</span>
							</div>
						</div>

						{/* Expanded detail */}
						{showDetail === c.id && (
							<div className="mt-3 pt-3 border-t border-stone-100 space-y-2 text-sm animate-slide-up">
								{c.beschreibung && (
									<p className="text-stone-600">{c.beschreibung}</p>
								)}
								<div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-stone-500">
									{c.website && (
										<a
											href={
												c.website.startsWith("http")
													? c.website
													: `https://${c.website}`
											}
											target="_blank"
											rel="noopener noreferrer"
											className="flex items-center gap-1 text-brand-600 hover:underline"
										>
											<Globe size={12} /> {c.website}
										</a>
									)}
									{c.mitarbeiter && (
										<span className="flex items-center gap-1">
											<Users size={12} /> {c.mitarbeiter} MA
										</span>
									)}
									<span>Erstellt: {formatDate(c.createdAt)}</span>
								</div>
								{c.tags && (
									<div className="flex flex-wrap gap-1">
										{c.tags.split(",").map((tag) => (
											<span
												key={tag.trim()}
												className="badge bg-stone-100 text-stone-500 text-xs"
											>
												{tag.trim()}
											</span>
										))}
									</div>
								)}
							</div>
						)}
					</div>
				))}
				{companies.length === 0 && !query.isLoading && (
					<div className="text-center py-12 text-stone-400">
						Keine Firmen gefunden.
					</div>
				)}
			</div>

			{/* Pagination */}
			{total > 50 && (
				<div className="flex items-center justify-between">
					<span className="text-xs text-stone-500">
						Seite {page + 1} von {Math.ceil(total / 50)}
					</span>
					<div className="flex gap-2">
						<button
							type="button"
							disabled={page === 0}
							onClick={() => setFilter("page", String(page - 1))}
							className="btn-ghost text-xs disabled:opacity-30"
						>
							Zurück
						</button>
						<button
							type="button"
							disabled={(page + 1) * 50 >= total}
							onClick={() => setFilter("page", String(page + 1))}
							className="btn-ghost text-xs disabled:opacity-30"
						>
							Weiter
						</button>
					</div>
				</div>
			)}
		</div>
	);
}
